import { useEffect, useState } from 'react';
import type { SubmitFunction } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'urql';
import {
  JobTemplateForm,
  unserialize,
} from '~/components/job-template/JobTemplateForm';
import { PopupDialogNew } from '~/components/ui/PopupDialogNew';
import {
  CreateJobTemplateDocument,
  CreateJobTemplateFormDocument,
  JobTemplateFieldsFragment,
  JobTemplateInput,
} from '~/gql/graphql';
import { JobTemplateListContext } from '~/routes/job-templates._index';
import { Option } from '~/types';
import { TemplateSelect } from './TemplateSelect';

type Props = {
  label?: string;
  error?: string | false;
  options: Option[];
  value: string;
  onChange: (newValue: string) => void;
  required?: boolean;
  optionsBoxHeight?: string;
  /** Optional job templates to determine asset-driven vs regular templates for grouping */
  jobTemplates?: readonly JobTemplateFieldsFragment[];
};

// Use generated typed documents to avoid runtime parsing issues

export function FindAddTemplate({
  label,
  error,
  options,
  value,
  onChange,
  required,
  optionsBoxHeight,
  jobTemplates,
}: Props) {
  const [localOptions, setLocalOptions] = useState<Option[]>(options);
  const [showDialog, setShowDialog] = useState(false);

  // Sync localOptions with options prop changes
  // This ensures localOptions stays up to date when parent updates the options
  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  const [{ data }] = useQuery({ query: CreateJobTemplateFormDocument });
  const [{ fetching: isCreating }, createTemplate] = useMutation(
    CreateJobTemplateDocument
  );

  const handleTemplateSubmit: SubmitFunction = async (formData) => {
    const input = unserialize(formData as FormData) as JobTemplateInput;
    try {
      const result = await createTemplate({ input });

      // Check for GraphQL errors
      if (result.error) {
        toast.error(
          result.error.message || 'Failed to create template. Please try again.'
        );
        return;
      }

      // Check if the created template has an ID
      if (!result.data?.createJobTemplate?.id) {
        toast.error('Failed to create template. Please try again.');
        return;
      }

      const newId = result.data.createJobTemplate.id;
      // Use safe label fallback to prevent undefined labels
      const safeLabel = input.name || 'Untitled Template';
      const newOption = { value: newId, label: safeLabel } as Option;
      setLocalOptions((prev) => [...prev, newOption]);
      onChange(newId);
      setShowDialog(false);
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div>
      <PopupDialogNew isOpen={showDialog} onClose={() => setShowDialog(false)}>
        <JobTemplateListContext.Provider
          value={{ templates: [], reload: () => {} }}
        >
          <JobTemplateForm
            data={{ integrations: data?.integrations ?? [] }}
            onClose={() => setShowDialog(false)}
            onSubmit={handleTemplateSubmit}
            isSubmittingExternal={isCreating}
          />
        </JobTemplateListContext.Provider>
      </PopupDialogNew>

      <TemplateSelect
        label={label}
        error={error}
        options={localOptions}
        value={value}
        onChange={onChange}
        onCreate={() => setShowDialog(true)}
        required={required}
        optionsBoxHeight={optionsBoxHeight}
        jobTemplates={jobTemplates}
      />
    </div>
  );
}
